import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useChangeBooleans, useComponentProperties, useResetActions, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useEffect, useState } from "react";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import AttributeTable from "../AttributeTable/AttributeTable";
import ActionTable from "../ActionTable/ActionTable";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";
import { useContext } from "react";
import DataFilter from "./Shared/DataFilter";

function Table() {

    const formProperties = componentPropertiesMap.Table;
    const { user } = useContext(GlobalContext).state;
    // hooks
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const advancedMode = usePropsStoreValue("advancedMode");
    const isInternalUser = useInternalUserCheck(user.email);
    const saveComponent = useSaveComponent()
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id)
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes()
    const resetContext = useResetComponentProperties();

    const [properties, setProperty] = useComponentProperties();
    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();
    const resetAttributes = useResetAttributeTable();
    const resetActions = useResetActions();

    const { t, i18n } = useTranslation([
        "display/components/table",
        "display/component_toolbar",
        "display/common"
    ], { useSuspense: false })

    // state
    const [tabSelected, setSelectedTab] = useState(0);

    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            setProperties();
        }
    }, [selectedPage, selectedComponent])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    // functions
    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function handleCheckboxChange(event) {
        setProperty(event.target.name, event.target.value);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "showFilter":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterLabel":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                case "filterAttributes":
                    if (
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined &&
                        selectedComponent[property] !== properties[property]
                    ) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar
                onSave={handleSave}
                onReset={resetComponent}
            />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    {
                        selectedPage.subPageType !== "Search" &&
                        <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                            <Scrollable>
                                <form className="p-2 edit2">
                                    {
                                        accessedFromDesigner &&
                                        <>
                                            <Checkbox
                                                disabled={selectedComponent.hidden}
                                                name="showLabel"
                                                checked={properties.showLabel}
                                                onChange={handleCheckboxChange}
                                                label={t("form_labels.show_label", { ns: "display/components/table" })}
                                                className="my-1 d-block"
                                            />

                                            <Checkbox
                                                disabled={selectedComponent.hidden}
                                                name="sortable"
                                                checked={properties.sortable}
                                                onChange={handleCheckboxChange}
                                                label={t("form_labels.sortable", { ns: "display/components/table" })}
                                                className="my-1 d-block"
                                            />
                                        </>
                                    }
                                    <div className={accessedFromDesigner ? "" : "ml-4"}>
                                        <Checkbox
                                            disabled={selectedComponent.hidden}
                                            name="allowExport"
                                            checked={properties.allowExport}
                                            onChange={handleCheckboxChange}
                                            label={t("form_labels.allow_export", { ns: "display/components/table" })}
                                            className="my-1 d-block"
                                        />
                                        <Checkbox
                                            disabled={selectedComponent.hidden}
                                            checked={properties.showLocalSearch}
                                            name={"showLocalSearch"}
                                            onChange={handleCheckboxChange}
                                            label={t("form_labels.local_search", { ns: "display/components/table" })}
                                            className="my-1 d-block"
                                        />
                                    </div>
                                    {!selectedPage.useAsReference && (
                                        <DataFilter />
                                    )}
                                </form>
                            </Scrollable>
                        </TabStripTab>
                    }
                    <TabStripTab title={t("tabs.properties", { ns: "display/common" })}>
                        <AttributeTable />
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default Table;